import { useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FactCheck as FactCheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Launch as LaunchIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";

import type {
  ArtifactSourceProvenance,
  SourceProvenanceReference,
} from "../../services/ArtifactService";
import { manifestPageLabel } from "../../utils/manifestPage";
import { canOpenArtifactRef, openArtifactSource } from "./artifactSourceNav";

interface GeneratedDocProvenanceProps {
  provenance: ArtifactSourceProvenance;
}

/**
 * Renders the source-provenance summary + clickable reference list for a
 * generated document (#20423 Story 5).
 *
 * Each reference opens its original source passage in the document viewer,
 * mirroring how an inline citation [N] deep-links (the same home-specific
 * record route — `openArtifactSource`/`navigateToRecord` resolve it the same
 * way the citation hyperlinks do). This is the "FROM [n]" affordance: a
 * reviewer can jump from a generated reference straight back to the cited
 * passage without redoing research.
 */
const GeneratedDocProvenance: React.FC<GeneratedDocProvenanceProps> = ({
  provenance,
}) => {
  const history = useHistory();

  // Reference prev/next navigation (mirrors AnnotationToolbar's stepper): a
  // compact "k / N" counter with arrows that scroll+focus each entry so a
  // reviewer can walk the provenance chain without hunting through the list.
  const [navPosition, setNavPosition] = useState<number | null>(null);
  const entryRefs = useRef(new Map<number, HTMLDivElement>());

  if (!provenance || provenance.references.length === 0) {
    return null;
  }

  const { claim_count, references } = provenance;

  // Distinct source documents behind the generated doc (spec §6.1: the summary
  // states how many sourced claims AND from how many documents). Prefer the
  // backend's authoritative count (distinct SDRs at generation time; survives
  // per-reviewer redaction as a bare number). Fall back to counting distinct
  // references for legacy manifests that predate the field.
  const documentCount =
    provenance.document_count > 0
      ? provenance.document_count
      : new Set(
          references.map(
            (r) => r.source_data_record_id ?? r.document_name ?? r.n,
          ),
        ).size;

  const stepReference = (delta: 1 | -1) => {
    const total = references.length;
    if (total === 0) return;
    const next =
      navPosition == null
        ? delta > 0
          ? 0
          : total - 1
        : (navPosition + delta + total) % total;
    setNavPosition(next);
    const el = entryRefs.current.get(references[next].n);
    el?.scrollIntoView({ block: "nearest" });
    el?.focus();
  };

  // Deep-link a reference into the annotated document viewer (shared with the
  // inline [n] markers in the generated-doc body, #20423 Story 5).
  const openSource = (ref: SourceProvenanceReference) =>
    openArtifactSource(history, ref, references);

  return (
    <Box
      data-testid="aa-generated-doc-provenance"
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
      }}
    >
      {/* Provenance summary + reference stepper */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <FactCheckIcon sx={{ fontSize: 18, color: "primary.main" }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Generated document
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {claim_count} sourced {claim_count === 1 ? "claim" : "claims"} · from{" "}
          {documentCount} {documentCount === 1 ? "document" : "documents"}
        </Typography>
        {references.length > 1 && (
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            data-testid="aa-provenance-ref-nav"
          >
            <IconButton
              size="small"
              onClick={() => stepReference(-1)}
              aria-label="Previous reference"
              data-attr="assistant:btn:prev-provenance-ref"
              sx={{ p: 0.25 }}
            >
              <ArrowUpIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 34, textAlign: "center" }}
              data-testid="aa-provenance-ref-nav-position"
            >
              {navPosition != null
                ? `${navPosition + 1} / ${references.length}`
                : `– / ${references.length}`}
            </Typography>
            <IconButton
              size="small"
              onClick={() => stepReference(1)}
              aria-label="Next reference"
              data-attr="assistant:btn:next-provenance-ref"
              sx={{ p: 0.25 }}
            >
              <ArrowDownIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Stack>
        )}
      </Stack>

      {/* Clickable "FROM [n]" reference list */}
      <Stack spacing={0.5}>
        {references.map((ref, index) => {
          const canOpen = canOpenArtifactRef(ref);
          const label = ref.document_name || `Source ${ref.n}`;
          const pageLabel = manifestPageLabel(ref.page);
          const pageSuffix = pageLabel ? `, p. ${pageLabel}` : "";

          return (
            <Stack
              key={ref.n}
              ref={(el: HTMLDivElement | null) => {
                if (el) entryRefs.current.set(ref.n, el);
                else entryRefs.current.delete(ref.n);
              }}
              tabIndex={-1}
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid={`aa-provenance-ref-${ref.n}`}
              sx={{
                borderRadius: 0.5,
                outline: "none",
                // Ring the stepper's current entry so "k / N" has a visible
                // anchor in the list.
                ...(navPosition === index && {
                  bgcolor: "action.selected",
                }),
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: -2,
                },
              }}
            >
              <Tooltip
                title={canOpen ? "Open the original source passage" : ""}
              >
                <Chip
                  label={`FROM [${ref.n}]`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={canOpen ? () => openSource(ref) : undefined}
                  icon={
                    canOpen ? (
                      <LaunchIcon sx={{ fontSize: "0.85rem" }} />
                    ) : undefined
                  }
                  data-attr={`assistant:link:provenance-ref-${ref.n}`}
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    cursor: canOpen ? "pointer" : "default",
                  }}
                />
              </Tooltip>
              {canOpen ? (
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => openSource(ref)}
                  sx={{ textAlign: "left", color: "text.secondary" }}
                >
                  {label}
                  {pageSuffix}
                </Link>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {label}
                  {pageSuffix}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default GeneratedDocProvenance;
